import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
    createRoleMap,
    deleteRoleMap,
    fetchAppRoles,
    restoreRoleMap,
    triggerCatalogSync,
    type AppRoleOption,
    type RoleMapRow,
} from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import type { RoleCoverageData } from './useRoleCoverageData';
import { buildRoleKeyByKey, formatRelativeDays } from './coverageHelpers';
import type { AppCoverageRow, EditorRoleRow, RoleKeyByKey } from './types';

/** A just-removed link, kept around for the undo window. */
interface PendingUndo {
    id: string;
    roleKey: string;
    appRole: string;
}

/** EAA-1817: purely a FRONTEND convention — `restoreRoleMap` has no
 *  server-side expiry, so this window is enforced client-side only. */
const UNDO_WINDOW_MS = 8000;

/**
 * EAA-1817: the selected app's editor card — the "Vai trò chuẩn | Vai trò
 * trong {app}" table, its add/remove/restore writes, the catalog-sync
 * trigger, and the undo affordance a removed chip offers.
 */
export function useRoleEditor(data: RoleCoverageData, selectedApp: AppCoverageRow | null) {
    const { apiBase, token, canManage, canSync, roleKeysQuery, roleMapsQuery, coverageQuery } =
        data;
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const [pendingUndos, setPendingUndos] = useState<PendingUndo[]>([]);
    const undoTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
    const [openPickerRoleKey, setOpenPickerRoleKey] = useState<string | null>(null);
    const [catalogViewerOpen, setCatalogViewerOpen] = useState(false);

    useEffect(() => {
        const timers = undoTimers.current;
        return () => {
            timers.forEach((timer) => clearTimeout(timer));
            timers.clear();
        };
    }, []);

    // GATE TRAP (see useRoleCoverageData.ts): `/admin/app-roles` is
    // IsSuperAdmin-only regardless of params — never fetch it for a caller
    // without `console.rolemap.manage`. Backs both the "+ vai trò" picker and
    // the "Xem danh mục" detail viewer.
    const appRolesQuery = useQuery({
        queryKey: ['admin-app-roles', selectedApp?.code, token],
        queryFn: () =>
            fetchAppRoles(apiBase, token as string, (selectedApp as AppCoverageRow).code),
        enabled: canManage && Boolean(selectedApp),
        retry: false,
    });

    const roleKeyByKey: RoleKeyByKey = useMemo(
        () => buildRoleKeyByKey(roleKeysQuery.data ?? []),
        [roleKeysQuery.data]
    );

    // EAA-1817: default-scope rows for the selected app only — matches the
    // coverage endpoint's own `organization__isnull=True` filter (see
    // useRoleCoverageData.ts) so chip ids/staleness agree with the matrix.
    const appDefaultRows = useMemo(() => {
        if (!selectedApp) return [];
        return (roleMapsQuery.data ?? []).filter(
            (row) => row.application === selectedApp.code && row.organization_id === null
        );
    }, [roleMapsQuery.data, selectedApp]);

    const staleCount = useMemo(
        () => appDefaultRows.filter((row) => row.is_stale).length,
        [appDefaultRows]
    );

    const editorRows = useMemo<EditorRoleRow[]>(() => {
        if (!selectedApp) return [];
        const roleKeys = coverageQuery.data?.role_keys ?? [];
        return roleKeys.map((roleKey) => {
            const vocab = roleKeyByKey.get(roleKey);
            const rowsForKey = appDefaultRows.filter((row) => row.role_key === roleKey);
            return {
                roleKey,
                roleName: vocab?.name ?? roleKey,
                roleDescription: vocab?.description ?? '',
                levelCount: vocab?.level_count ?? 0,
                linkCount: vocab?.link_count ?? 0,
                chips: rowsForKey
                    .filter(
                        (row): row is RoleMapRow & { app_role: string } => row.app_role !== null
                    )
                    .map((row) => ({ id: row.id, appRole: row.app_role, isStale: row.is_stale })),
            };
        });
    }, [selectedApp, coverageQuery.data, roleKeyByKey, appDefaultRows]);

    const invalidateAfterWrite = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ['role-map-coverage'] });
        void queryClient.invalidateQueries({ queryKey: ['role-maps'] });
        void queryClient.invalidateQueries({ queryKey: ['role-keys'] });
    }, [queryClient]);

    const [writeError, setWriteError] = useState<string | null>(null);
    const reportError = useCallback(
        (fallback: string) => (error: unknown) =>
            setWriteError(error instanceof OrgAdminApiError ? error.message : fallback),
        []
    );

    const addMutation = useMutation({
        mutationFn: (input: { roleKey: string; appRole: string }) =>
            createRoleMap(apiBase, token as string, {
                role_key: input.roleKey,
                application: (selectedApp as AppCoverageRow).code,
                app_role: input.appRole,
            }),
        onSuccess: () => {
            setWriteError(null);
            invalidateAfterWrite();
        },
        onError: reportError(t('console.roleCoverage.errors.addRoleFailed')),
    });

    const removeMutation = useMutation({
        mutationFn: (row: { id: string; roleKey: string; appRole: string }) =>
            deleteRoleMap(apiBase, token as string, row.id).then(() => row),
        onSuccess: (row) => {
            setWriteError(null);
            invalidateAfterWrite();
            setPendingUndos((prev) => [
                ...prev,
                { id: row.id, roleKey: row.roleKey, appRole: row.appRole },
            ]);
            const timer = setTimeout(() => {
                setPendingUndos((prev) => prev.filter((undo) => undo.id !== row.id));
                undoTimers.current.delete(row.id);
            }, UNDO_WINDOW_MS);
            undoTimers.current.set(row.id, timer);
        },
        onError: reportError(t('console.roleCoverage.errors.removeRoleFailed')),
    });

    const restoreMutation = useMutation({
        mutationFn: (id: string) => restoreRoleMap(apiBase, token as string, id),
        onSuccess: (_result, id) => {
            setWriteError(null);
            invalidateAfterWrite();
            const timer = undoTimers.current.get(id);
            if (timer) clearTimeout(timer);
            undoTimers.current.delete(id);
            setPendingUndos((prev) => prev.filter((undo) => undo.id !== id));
        },
        onError: reportError(t('console.roleCoverage.errors.undoFailed')),
    });

    const syncMutation = useMutation({
        mutationFn: () => triggerCatalogSync(apiBase, token as string),
        onSuccess: invalidateAfterWrite,
        onError: reportError(t('console.roleCoverage.errors.syncFailed')),
    });

    function addRole(roleKey: string, appRole: string) {
        addMutation.mutate({ roleKey, appRole });
        setOpenPickerRoleKey(null);
    }

    function removeChip(roleKey: string, chip: { id: string; appRole: string }) {
        removeMutation.mutate({ id: chip.id, roleKey, appRole: chip.appRole });
    }

    function undoRemove(id: string) {
        restoreMutation.mutate(id);
    }

    const catalogAgeLabel = selectedApp
        ? formatRelativeDays(
              (data.adminAppsQuery.data ?? []).find((app) => app.code === selectedApp.code)
                  ?.catalog_last_synced_at ?? null,
              t
          )
        : '';

    return {
        editorRows,
        staleCount,
        totalLinks: appDefaultRows.length,
        catalogAgeLabel,
        appRoleOptions: (appRolesQuery.data ?? []) as AppRoleOption[],
        appRolesLoading: appRolesQuery.isLoading,
        canOpenCatalog: canManage,
        canSync,
        openPickerRoleKey,
        setOpenPickerRoleKey,
        catalogViewerOpen,
        setCatalogViewerOpen,
        addRole,
        addPending: addMutation.isPending,
        removeChip,
        removePending: removeMutation.isPending,
        pendingUndos,
        undoRemove,
        writeError,
        syncMutation,
    };
}

export type RoleEditorVM = ReturnType<typeof useRoleEditor>;
